import { useSelector } from 'react-redux';
import { getImageByProject } from '../../Store/imageQueueSlice';
import { RootState } from '../../Store/store';

interface MinimizedUploadProgressProps {
  projectId: string;
  onMaximize: () => void;
}

const MinimizedUploadProgress = ({ projectId, onMaximize }: MinimizedUploadProgressProps) => {
  const imageQueue = useSelector((state: RootState) => getImageByProject(state, projectId));
  const inProgressImages = imageQueue.inprog;
  const totalImages = inProgressImages.length + imageQueue.queue.length;
  const currentImage = inProgressImages[0];
  
  // Calculate overall progress
  const overallProgress = inProgressImages.reduce((acc, img) => acc + (img.progress || 0), 0) / totalImages;

  if (inProgressImages.length === 0) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 w-80 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer"
      onClick={onMaximize}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Uploading {inProgressImages.length} of {totalImages} images
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {currentImage?.name}
          </span>
        </div>
        
        {/* Overall progress bar */}
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-600 transition-all duration-300 ease-in-out"
            style={{ width: `${overallProgress}%` }}
            role="progressbar"
            aria-valuenow={overallProgress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        
        {/* Individual file progress */}
        <div className="mt-2 space-y-2">
          {inProgressImages.map((img) => (
            <div key={img.imageId} className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all duration-300 ease-in-out"
                  style={{ width: `${img.progress || 0}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {img.progress || 0}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MinimizedUploadProgress; 